import React from 'react';
import { SectionHeading } from './primitives';
import { ACTION_LABELS } from './profileConstants';
import type { ActivityEntry } from './types';
import LoadingStatus from '@/components/ui/LoadingStatus';

interface RecentActivityCardProps {
  activity: ActivityEntry[];
  activityLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  formatDateTime: (iso: string) => string;
}

const RecentActivityCard: React.FC<RecentActivityCardProps> = ({
  activity,
  activityLoading,
  hasMore,
  onLoadMore,
  formatDateTime,
}) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8 dark:border-gray-700 dark:bg-gray-800">
    <SectionHeading
      icon={
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      }
    >
      Recent Activity
      <span className="ml-1 font-normal normal-case tracking-normal text-gray-400 dark:text-gray-500">· last 30 days</span>
    </SectionHeading>

    {activity.length === 0 && !activityLoading ? (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 py-10 text-center dark:border-gray-700">
        <svg className="h-8 w-8 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-300">No recent activity</p>
        <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
          Logins, password changes and key actions will appear here.
        </p>
      </div>
    ) : (
      <ul className="space-y-1">
        {activity.map(entry => {
          const actionInfo = ACTION_LABELS[entry.action] || {
            label: entry.action,
            color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
          };
          return (
            <li
              key={entry.id}
              className="flex items-start gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/40"
            >
              <span className={`mt-0.5 inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-xs font-medium ${actionInfo.color}`}>
                {actionInfo.label}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-gray-900 dark:text-gray-100">{entry.description}</p>
                {entry.entityType && (
                  <p className="text-xs capitalize text-gray-500 dark:text-gray-400">{entry.entityType.replace(/_/g, ' ')}</p>
                )}
              </div>
              <span className="shrink-0 whitespace-nowrap text-xs text-gray-400 dark:text-gray-500">
                {formatDateTime(entry.createdAt)}
              </span>
            </li>
          );
        })}
      </ul>
    )}

    {activityLoading && <LoadingStatus label="Loading recent activity" className="mt-3" />}
    {hasMore && !activityLoading && activity.length > 0 && (
      <button
        onClick={onLoadMore}
        className="mt-4 text-sm font-medium text-primary-600 transition hover:text-primary-700 hover:underline dark:text-primary-400"
      >
        Load more
      </button>
    )}
  </div>
);

export default RecentActivityCard;
